import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Modal } from '@/components/ui';
import { format, parseISO } from 'date-fns';
import { FilterableDataTable } from './FilterableDataTable';
import type { FilterableColumn } from './FilterableDataTable';
import {
  useArpSignals,
  useCertainSignals,
  useRegistrationLog,
} from '../hooks/useRegistrationLog';
import type { ArpSignalRow, CertainSignalRow, RegistrationLogRow } from '../types';

interface RegistrationsLogPanelProps {
  searchText: string;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'M/d/yyyy h:mm a');
  } catch {
    return iso;
  }
}

const certainColumns: FilterableColumn<CertainSignalRow>[] = [
  { key: 'registrationStatus', header: 'Registration Status' },
  {
    key: 'logCreatedDate',
    header: 'Log Created Date',
    accessor: (r) => (r.logCreatedDate ? new Date(r.logCreatedDate) : null),
    render: (r) => formatDateTime(r.logCreatedDate),
  },
  { key: 'registrationDateModified', header: 'Registration Date Modified' },
];

const arpColumns: FilterableColumn<ArpSignalRow>[] = [
  { key: 'messageType', header: 'Message Type' },
  { key: 'registrationStatus', header: 'Registration Status' },
  {
    key: 'logCreatedDate',
    header: 'Log Created Date',
    accessor: (r) => (r.logCreatedDate ? new Date(r.logCreatedDate) : null),
    render: (r) => formatDateTime(r.logCreatedDate),
  },
  {
    key: 'modifiedDate',
    header: 'Modified Date',
    accessor: (r) => (r.modifiedDate ? new Date(r.modifiedDate) : null),
    render: (r) => formatDateTime(r.modifiedDate),
  },
];

export function RegistrationsLogPanel({ searchText }: RegistrationsLogPanelProps) {
  const { data: rows = [], isLoading, isError } = useRegistrationLog(searchText);
  const [certainRegCode, setCertainRegCode] = useState<string | null>(null);
  const [arpRegCode, setArpRegCode] = useState<string | null>(null);

  const certainQuery = useCertainSignals(certainRegCode);
  const arpQuery = useArpSignals(arpRegCode);

  const columns: FilterableColumn<RegistrationLogRow>[] = [
    { key: 'accountCode', header: 'Name' },
    { key: 'eventCode', header: 'Event Code' },
    { key: 'registrationCode', header: 'Registration Code' },
    { key: 'emailId', header: 'Email Id' },
    { key: 'lastName', header: 'Last Name' },
    { key: 'firstName', header: 'First Name' },
    { key: 'registrationStatus', header: 'Registration Status' },
    { key: 'attendeeType', header: 'Attendee Type' },
    {
      key: 'certainLog',
      header: 'Certain Log',
      sortable: false,
      filterable: false,
      align: 'center',
      render: (row) => (
        <button
          type="button"
          onClick={() => setCertainRegCode(row.registrationCode)}
          aria-label={`Open Certain log for ${row.registrationCode}`}
          className="inline-flex items-center justify-center rounded p-1 text-cool-gray-11 hover:bg-gray-100 hover:text-black focus:outline-none focus:ring-2 focus:ring-black"
        >
          <ExternalLink size={16} aria-hidden="true" />
        </button>
      ),
    },
    {
      key: 'aRPLog',
      header: 'ARP Log',
      sortable: false,
      filterable: false,
      align: 'center',
      render: (row) => (
        <button
          type="button"
          onClick={() => setArpRegCode(row.registrationCode)}
          aria-label={`Open ARP log for ${row.registrationCode}`}
          className="inline-flex items-center justify-center rounded p-1 text-cool-gray-11 hover:bg-gray-100 hover:text-black focus:outline-none focus:ring-2 focus:ring-black"
        >
          <ExternalLink size={16} aria-hidden="true" />
        </button>
      ),
    },
  ];

  return (
    <section aria-labelledby="registration-log-heading" className="space-y-3">
      <div>
        <h2 id="registration-log-heading" className="text-lg font-semibold leading-7 text-black">
          Registration Log
        </h2>
        {searchText ? (
          <p className="text-sm leading-6 text-cool-gray-11">Filtered by {searchText}</p>
        ) : null}
      </div>

      {isError ? (
        <div
          role="alert"
          className="border border-dds-red bg-red-50 p-3 text-sm text-red-700"
        >
          Failed to load registrations. Please try again.
        </div>
      ) : null}

      <FilterableDataTable
        data={rows}
        columns={columns}
        keyExtractor={(r) =>
          `${r.accountCode}|${r.eventCode}|${r.registrationCode}|${r.emailId}`
        }
        searchPlaceholder="Search by Registration Code..."
        ariaLabel="Registration log"
        isLoading={isLoading}
        emptyMessage="No registrations found."
      />

      <Modal
        isOpen={certainRegCode !== null}
        onClose={() => setCertainRegCode(null)}
        title="Certain Log Signals"
        className="max-w-4xl"
      >
        {certainQuery.isLoading ? (
          <p className="text-sm text-cool-gray-11">Loading…</p>
        ) : certainQuery.isError ? (
          <p role="alert" className="text-sm text-red-700">
            Failed to load Certain signals.
          </p>
        ) : (
          <FilterableDataTable
            data={certainQuery.data ?? []}
            columns={certainColumns}
            keyExtractor={(r) => `${r.registrationStatus}|${r.logCreatedDate}`}
            ariaLabel="Certain log signals"
            bodyMaxHeightClass="max-h-[400px]"
            emptyMessage="No Certain signals found."
          />
        )}
      </Modal>

      <Modal
        isOpen={arpRegCode !== null}
        onClose={() => setArpRegCode(null)}
        title="ARP Log Signals"
        className="max-w-4xl"
      >
        {arpQuery.isLoading ? (
          <p className="text-sm text-cool-gray-11">Loading…</p>
        ) : arpQuery.isError ? (
          <p role="alert" className="text-sm text-red-700">
            Failed to load ARP signals.
          </p>
        ) : (
          <FilterableDataTable
            data={arpQuery.data ?? []}
            columns={arpColumns}
            keyExtractor={(r) =>
              `${r.messageType}|${r.registrationStatus}|${r.logCreatedDate}|${r.modifiedDate}`
            }
            ariaLabel="ARP log signals"
            bodyMaxHeightClass="max-h-[400px]"
            emptyMessage="No ARP signals found."
          />
        )}
      </Modal>
    </section>
  );
}
